/**
 * Marketing Scripts Hub — private admin page at /app/marketing-scripts.
 *
 * Renders the launch-swipe markdown files from /app/memory/ with per-section
 * "Copy" buttons so Tracy can grab any post/email/script from any device
 * without SSH'ing into the box.
 *
 * The markdown is split on H2 (`## `) boundaries so each section (an email,
 * a tweet, a video script) gets its own copy affordance.
 */
import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Video, Megaphone, BookOpen, Copy, Check, Loader2, FileText,
  ArrowLeft, Download,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ICON_MAP = { video: Video, megaphone: Megaphone, book: BookOpen };

/** Split markdown into copy-able chunks, one per H2 (`## `). */
function splitSections(md) {
  if (!md) return [];
  const lines = md.split("\n");
  const chunks = [];
  let current = { heading: "Intro", body: [] };
  for (const line of lines) {
    if (line.startsWith("## ") && !line.startsWith("### ")) {
      if (current.body.length > 0 || current.heading !== "Intro") {
        chunks.push({ ...current, body: current.body.join("\n").trim() });
      }
      current = { heading: line.replace(/^##\s+/, "").trim(), body: [] };
    } else {
      current.body.push(line);
    }
  }
  if (current.body.length > 0) {
    chunks.push({ ...current, body: current.body.join("\n").trim() });
  }
  return chunks;
}

function CopyButton({ text, label = "Copy", className = "" }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Couldn't copy — select and Ctrl+C manually");
    }
  };
  return (
    <button
      type="button"
      onClick={doCopy}
      data-testid={`marketing-copy-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold border border-[var(--line)] hover:bg-[var(--primary)]/10 hover:border-[var(--primary)] transition ${className}`}
    >
      {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
      {copied ? "Copied!" : label}
    </button>
  );
}

export default function MarketingScripts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [assets, setAssets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load asset catalog on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/marketing-assets/");
        setAssets(data.assets || []);
        const urlKey = searchParams.get("asset");
        if (urlKey && (data.assets || []).some((a) => a.key === urlKey)) {
          setSelected(urlKey);
        }
      } catch {
        toast.error("Couldn't load marketing assets");
      }
    })();
  }, []);

  // Load individual asset content when selection changes
  useEffect(() => {
    if (!selected) { setContent(null); return; }
    setLoading(true);
    setSearchParams({ asset: selected });
    api.get(`/admin/marketing-assets/${selected}`)
      .then(({ data }) => setContent(data))
      .catch(() => toast.error("Couldn't load that asset"))
      .finally(() => setLoading(false));
  }, [selected]);

  const sections = useMemo(() => splitSections(content?.content), [content]);

  // ─── Catalog view ────────────────────────────────────────────────────────
  if (!selected) {
    return (
      <div className="max-w-5xl mx-auto p-6 md:p-8" data-testid="marketing-scripts-catalog">
        <div className="mb-8">
          <div className="text-[10px] font-black tracking-[0.18em] uppercase text-[var(--primary)] mb-2">
            Founder Toolkit
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
            Marketing Scripts Hub
          </h1>
          <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
            Every launch script, email, tweet, and video outline in one place. Pick a kit, hit
            Copy on any section, paste into your app of choice, ship.
          </p>
        </div>

        {assets.length === 0 ? (
          <div className="p-8 rounded-xl border border-dashed border-[var(--line)] text-center text-[var(--ink-3)]">
            <Loader2 size={20} className="animate-spin inline mr-2" />
            Loading assets…
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {assets.map((a) => {
              const Icon = ICON_MAP[a.icon] || FileText;
              return (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => setSelected(a.key)}
                  data-testid={`marketing-asset-${a.key}`}
                  className="text-left p-6 rounded-xl border-2 border-[var(--line)] hover:border-[var(--primary)] hover:bg-[var(--primary)]/5 transition group"
                >
                  <div className="w-11 h-11 rounded-lg bg-[var(--ink)] text-white flex items-center justify-center mb-4 group-hover:bg-[var(--primary)] group-hover:text-[var(--ink)] transition">
                    <Icon size={20} />
                  </div>
                  <div className="font-display font-black text-xl mb-1.5">{a.title}</div>
                  <div className="text-sm text-[var(--ink-2)] leading-snug">{a.description}</div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ─── Detail view ─────────────────────────────────────────────────────────
  return (
    <div className="max-w-4xl mx-auto p-6 md:p-8" data-testid="marketing-scripts-detail">
      <button
        type="button"
        onClick={() => setSelected(null)}
        data-testid="marketing-back-btn"
        className="inline-flex items-center gap-1.5 text-sm text-[var(--ink-2)] hover:text-[var(--ink)] mb-4"
      >
        <ArrowLeft size={14} /> Back to all kits
      </button>

      {loading && (
        <div className="flex items-center gap-2 text-[var(--ink-3)]">
          <Loader2 size={16} className="animate-spin" /> Loading script…
        </div>
      )}

      {content && (
        <>
          <div className="mb-6 pb-6 border-b border-[var(--line)]">
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-2">
              {content.title}
            </h1>
            <p className="text-[var(--ink-2)] max-w-2xl">{content.description}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              <CopyButton text={content.content} label="Copy entire kit" />
              <a
                href={`data:text/markdown;charset=utf-8,${encodeURIComponent(content.content)}`}
                download={`${content.key}.md`}
                data-testid="marketing-download-md"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold border border-[var(--line)] hover:bg-[var(--primary)]/10 hover:border-[var(--primary)] transition"
              >
                <Download size={12} /> Download .md
              </a>
              <span className="text-xs text-[var(--ink-3)] self-center ml-2">
                {Math.round(content.size_bytes / 1024)} KB · {sections.length} sections
              </span>
            </div>
          </div>

          {/* Per-section render with individual copy buttons */}
          <div className="space-y-6">
            {sections.map((s, i) => (
              <div
                key={`${i}-${s.heading}`}
                className="rounded-xl border border-[var(--line)] overflow-hidden bg-white"
                data-testid={`marketing-section-${i}`}
              >
                <div className="flex items-center justify-between gap-3 px-5 py-3 bg-slate-50 border-b border-[var(--line)]">
                  <div className="font-display font-black text-base md:text-lg leading-tight flex-1 min-w-0 break-words">
                    {s.heading}
                  </div>
                  <CopyButton text={s.body} label="Copy section" />
                </div>
                <pre className="px-5 py-4 text-sm leading-relaxed whitespace-pre-wrap font-sans text-[var(--ink)] overflow-x-auto">
                  {s.body}
                </pre>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
